import React from "react";
import { Button } from "../ui/button";
import { Delete, X } from "lucide-react";

/**
 * Full-screen touch numeric keypad for factory-floor operators.
 */
export default function TouchNumericKeypad({
  open,
  title,
  value,
  onChange,
  onConfirm,
  onClose,
  suffix = "",
  allowDecimal = true,
  testId = "touch-keypad",
}) {
  if (!open) return null;

  const append = (digit) => {
    if (digit === "." && (!allowDecimal || value.includes("."))) return;
    if (value === "" && digit === ".") {
      onChange("0.");
      return;
    }
    onChange(`${value}${digit}`);
  };

  const backspace = () => onChange(value.slice(0, -1));

  const keys = allowDecimal
    ? ["7", "8", "9", "4", "5", "6", "1", "2", "3", ".", "0", "⌫"]
    : ["7", "8", "9", "4", "5", "6", "1", "2", "3", "C", "0", "⌫"];

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-bansal-ink/95 text-white"
      data-testid={testId}
    >
      <div className="flex items-center justify-between px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-white/60">{title}</p>
          <p className="font-mono text-4xl sm:text-5xl tabular-nums mt-2 min-h-[3rem]">
            {value || "—"}
            {suffix && value ? <span className="text-2xl text-white/70 ml-2">{suffix}</span> : null}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-14 w-14 text-white hover:bg-accent/10"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-8 w-8" />
        </Button>
      </div>

      <div className="flex-1 grid grid-cols-3 gap-2 p-3 sm:p-4 max-w-lg mx-auto w-full content-start">
        {keys.map((key) => (
          <button
            key={key}
            type="button"
            className="min-h-[72px] sm:min-h-[80px] rounded-2xl text-3xl font-semibold bg-card/10 active:bg-card/25 border border-white/10 touch-manipulation"
            onClick={() => {
              if (key === "⌫") backspace();
              else if (key === "C") onChange("");
              else append(key);
            }}
          >
            {key === "⌫" ? <Delete className="h-8 w-8 mx-auto" /> : key}
          </button>
        ))}
      </div>

      <div className="p-4 pb-[max(1rem,env(safe-area-inset-bottom))] grid grid-cols-2 gap-3 max-w-lg mx-auto w-full">
        <Button
          type="button"
          variant="outline"
          className="min-h-[64px] text-lg border-white/30 text-white hover:bg-accent/10"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="min-h-[64px] text-lg bg-bansal-sage hover:bg-bansal-sageDark text-white"
          onClick={onConfirm}
          disabled={!value}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
}
